/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observable } from 'mobx';
import * as React from 'react';
import { useAutorunState } from 'src/core/hooks/useAutorunState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { SuggestUtterancesFilter } from 'src/luis/projects/utterances/components/SuggestUtterancesToolbar';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import {
    generateSuggestedUtterancesObservables,
    SuggestedUtteranceObservable
} from 'src/luis/projects/utterances/utils/generateSuggestedUtterancesObservables';
import { LuisId } from 'src/luis/types/Resource';

export type UseSuggestedUtteranceReturn = {
    isLoading: boolean;
    suggestedUtterancesObservables: SuggestedUtteranceObservable[];
    reload: () => void;
};

/**
 * A hook for subscribing to utterances belonging to a specific model.
 * This hook fetches and constructs Utterance objects using the denormalized entities returned
 * from `useEntities` hook.
 */
export const useSuggestedUtterances = (filter: SuggestUtterancesFilter): UseSuggestedUtteranceReturn => {
    const { dispatcher, rootStore } = useGlobalState();
    const {
        currentLuisAppStore: {
            currentAppVersionStore: { intentStoreHouse, entityStoreHouse, phraseListStoreHouse, suggestedUtteranceStoreHouse }
        }
    } = rootStore;

    const [localUtterancesMapStore] = React.useState(() => observable(new Map<LuisId, UtteranceStore>()));

    const initFunc = React.useCallback(
        () =>
            generateSuggestedUtterancesObservables({
                localUtterancesMapStore,
                intentStoreHouse,
                entityStoreHouse,
                phraseListStoreHouse,
                suggestedUtteranceStoreHouse
            }),
        [localUtterancesMapStore, intentStoreHouse, entityStoreHouse, phraseListStoreHouse, suggestedUtteranceStoreHouse]
    );

    const suggestedUtterancesObservables = useAutorunState(initFunc, []);

    const fetchData = () => {
        let requestObj: { resource: 'entities' | 'intents'; resourceId: LuisId; roleId?: LuisId };
        if (filter.filterType === 'roles') {
            const entityFilter = entityStoreHouse.items.find(entityStore => entityStore.entityRoleStoreHouse.findById(filter.filterId));
            requestObj = {
                resource: 'entities',
                resourceId: entityFilter.id,
                roleId: filter.filterId
            };
        } else if (filter.filterType === 'entities') {
            requestObj = {
                resource: 'entities',
                resourceId: filter.filterId
            };
        } else {
            requestObj = { resource: 'intents', resourceId: filter.filterId };
        }

        dispatcher.dispatch('loadSuggestedUtterances', { ...requestObj, localUtterancesMapStore });
    };

    React.useEffect(() => {
        if (filter) {
            fetchData();
        }
    }, [filter]);

    const hasFinishedTransforming = suggestedUtteranceStoreHouse.items.length > 0 ? suggestedUtterancesObservables.length > 0 : true;

    if (suggestedUtteranceStoreHouse.isLoading || !hasFinishedTransforming) {
        return { isLoading: true, suggestedUtterancesObservables: [], reload: fetchData };
    }

    return {
        isLoading: false,
        suggestedUtterancesObservables,
        reload: fetchData
    };
};
